import { inject } from '@angular/core';
import { map, tap } from 'rxjs';
import { patchState, signalStore, withMethods } from '@ngrx/signals';
import {
  removeEntity,
  updateEntity,
  withEntities,
} from '@ngrx/signals/entities';
import { withCursorPagination } from '@dgacontrol-frontend/shared-util-cursor-pagination';

import { withChargesListFilters } from './charges-list-filters.feature';

import { BillingSubscriptionChargeService } from './generated/billing-subscription-charge/billing-subscription-charge.service';
import type {
  BillingV1SubscriptionChargesListParams,
  PatchedSubscriptionChargePatchRequestV1Request,
  SubscriptionChargeCreateRequestV1Request,
  SubscriptionChargeTriggerRequestV1Request,
  SubscriptionChargeV1,
} from './generated/types';

/**
 * TARG-4 pure: injects `BillingSubscriptionChargeService` directly, no facade.
 * Same shape as `BillingItemsStore` + `SubscriptionsStore` (iters 1-2).
 *
 * `triggerCharges` is the batch emission path (POST /trigger/) that fans out
 * one charge per selected user; `createSubscriptionCharge` is the RESTful
 * one-off admin correction (POST /). Both fire a `store.load({})` refetch —
 * inserting locally would desync the server-side cursor order.
 *
 * COMPONENT-DRIVEN: `dt-subscription-charges-modal` calls `load({subscription_id})`
 * on init to filter to the drill-down; the store does NOT auto-load.
 */
export const SubscriptionChargesStore = signalStore(
  withEntities<SubscriptionChargeV1>(),
  withCursorPagination<
    SubscriptionChargeV1,
    BillingV1SubscriptionChargesListParams
  >(() => {
    const api = inject(BillingSubscriptionChargeService);
    // The generated page already is { results, next, previous }: no adapter.
    return (query, cursor) =>
      api.billingV1SubscriptionChargesList({ ...query, cursor: cursor ?? undefined });
  }),
  withChargesListFilters(),
  withMethods((store, api = inject(BillingSubscriptionChargeService)) => ({
    createSubscriptionCharge(body: SubscriptionChargeCreateRequestV1Request) {
      return api.billingV1SubscriptionChargesCreate(body).pipe(
        tap(() => {
          store.load({});
        }),
        map((res) => res.subscription_charge),
      );
    },
    updateSubscriptionCharge(
      body: PatchedSubscriptionChargePatchRequestV1Request,
      identity: string,
    ) {
      return api
        .billingV1SubscriptionChargesPartialUpdate({ identity }, body)
        .pipe(
          tap((res) =>
            { patchState(
              store,
              updateEntity({
                id: res.subscription_charge.id,
                changes: res.subscription_charge,
              }),
            ); },
          ),
          map((res) => res.subscription_charge),
        );
    },
    deleteSubscriptionCharge(identity: string, id: number) {
      return api.billingV1SubscriptionChargesDestroy({ identity }).pipe(
        tap(() => {
          patchState(store, removeEntity(id));
        }),
      );
    },
    markPaid(identity: string, id: number) {
      return api.billingV1SubscriptionChargesPaidMark({ identity }).pipe(
        tap((res) =>
          { patchState(
            store,
            updateEntity({ id, changes: res.subscription_charge }),
          ); },
        ),
        map((res) => res.subscription_charge),
      );
    },
    unmarkPaid(identity: string, id: number) {
      return api.billingV1SubscriptionChargesPaidUnmark({ identity }).pipe(
        tap((res) =>
          { patchState(
            store,
            updateEntity({ id, changes: res.subscription_charge }),
          ); },
        ),
        map((res) => res.subscription_charge),
      );
    },
    triggerCharges(body: SubscriptionChargeTriggerRequestV1Request) {
      return api.billingV1SubscriptionChargesTrigger(body).pipe(
        tap(() => {
          store.load({});
        }),
        map((res) => res.identities),
      );
    },
    // GET → binary PDF (`responseType: 'blob'` in the generated client). Nothing
    // to patch: the PDF is rendered on the fly and never stored (M-bill-3).
    invoicePdf(identity: string) {
      return api.billingSubscriptionChargeInvoicePdfRetrieve({ identity });
    },
  })),
);
